import { useFileUpdate } from '@risksmart-app/components/src/file/useFileUpdate';
import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';

import { useInsertAsset } from '@/hooks/mutations/asset';
import { assetDetailsUrl } from '@/utils/urls';

import AssetForm from '../../update/forms/AssetForm';
import type { AssetFormFieldData } from '../../update/forms/assetSchema';

const AssetCreateTab: FC = () => {
  useI18NSummaryHelpContent('assets.help');
  const navigate = useNavigate();
  const { updateFiles } = useFileUpdate();
  const { insertAsset } = useInsertAsset();

  const onSave = async (values: AssetFormFieldData) => {
    const result = await insertAsset({
      Title: values.Title,
      Status: values.Status,
      Description: values.Description ?? null,
      AssetType: values.AssetType ?? null,
      SerialNumber: values.SerialNumber ?? null,
      PurchaseDate: values.PurchaseDate ?? null,
      Location: values.Location ?? null,
      Cost: values.Cost ?? null,
      DepreciationMethod: values.DepreciationMethod ?? null,
      Assignee: values.Assignee?.value ?? null,
      CustomAttributeData: values.CustomAttributeData ?? undefined,
      DepartmentTypeIds:
        values.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
      ...ownerAndContributorIds(values),
    });
    const assetId = result?.Id;
    if (!assetId) {
      throw new Error('Missing assetId');
    }
    await updateFiles({
      parentType: Parent_Type_Enum.Asset,
      parentId: assetId,
      originalFiles: values.files?.filter(
        (f) => !(f instanceof File)
      ) as RelationFile[],
      selectedFiles: values.files,
    });
    navigate(assetDetailsUrl(assetId), { replace: true });
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return <AssetForm onSave={onSave} onDismiss={onDismiss} />;
};

export default AssetCreateTab;
